import React from 'react';
import { View } from 'react-native'
import { Route, Switch, Redirect } from './index';

import Home from 'src/screens/Home/Home'
import DNAMyFolders from 'src/screens/MyFolders/DNAMyFolders'
import DNALibrary from 'src/screens/MyLibrary/DNALibrary'
import DNABookmarks from 'src/screens/Bookmarks/DNABookmarks'
import DNANotifications from 'src/screens/Notifications/Notifications'
import DNAMeetingsHistory from 'src/screens/MeetingsHistory/DNAMeetingsHistory'
import Profile from 'src/screens/Profile/Profile';

import Playground from '../screens/Playground';
import PublisherReports from '../screens/Reports/PublisherReports';
import ViewerReports from '../screens/Reports/ViewerReports';
import Documents from '../screens/Documents';
import Tenants from '../screens/Tenants/Tenants';
import EmailTemplates from '../screens/EmailTemplates/EmailTemplates';
import Meetings from '../screens/Meetings/Meetings';
import FAQ from 'src/screens/FAQ/FAQ'
import SyncStatusDebug from 'src/screens/Debug/SyncStatus'

import { useCurrentUser } from 'src/state/redux/selector/user';
import { UserRole } from '@alucio/aws-beacon-amplify/src/models';
import BulletinHistory from 'src/screens/Bulletin/BulletinHistory';
import NotFound from 'src/components/DNA/Error/DNAErrorPage';

import { ROUTES } from './routeDef'
import { useHistory, RouteComponentProps } from 'react-router';
import PublisherSearchResults from 'src/screens/PublisherSearchResults/PublisherSearchResults';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import PopoutContent from 'src/screens/Meetings/PopoutContent/PopoutContent';
import AdminSettings from 'src/screens/AdminSettings/AdminSettings';
import PublisherCompliance from 'src/screens/PublisherCompliance/PublisherCompliance';
import DNAHubList from 'src/screens/Hubs/DNAHubList';
import Debug from 'src/screens/Debug';
import DeviceDebug from 'src/screens/DeviceDebug';
import HubsTemplateList from 'src/screens/Hubs/HubTemplates/HubsTemplateList';
import loggers from 'src/utils/logger';
import DNAFavoriteFolders from 'src/screens/MyFolders/DNAFavoriteFolders';
import {
  ExternalOidcVaultCrmSsoCallback,
  ExternalOidcVaultCrmSsoStart,
} from 'src/screens/SSO/ExternalOidcVaultCrmSso';

export { ROUTES } from './routeDef'
export type { DNARoute, PAGES } from './routeDef'

const ProtectedRoute = ({ allowedRoles, component: Component, onInvalidRedirectTo, ...rest }) => {
  // [TODO] Make this more configurable later, currently a workaround for performance
  const { userProfile: user } = useCurrentUser();
  const canUserAccess = allowedRoles.includes(user?.role) ||
    (user?.additionalRoles as UserRole[])?.some((role) => allowedRoles.includes(role)); // as assertion is needed because of how additionalRoles was typed in the codegen
  if (!canUserAccess) {
    loggers.util.warn('User does not have access to this route', {
      user,
      allowedRoles,
      canUserAccess,
    })
  }
  return (
    <Route
      {...rest}
      render={(props) => (
        canUserAccess
          ? <Component {...props} />
          : <Redirect to={onInvalidRedirectTo} />
      )}
    />
  )
}
// [TODO]: This is a workaround to improve perceived smoothness
//  This is due to slow first render times on new pages
//  Most likely due to UI Kitten :(
const PerformanceWorkaroundRoute = () => {
  return (
    <View style={{ flex: 1 }} />
  )
}

const PublisherSearchResultsPage = (props: RouteComponentProps) => (
  <PublisherSearchResults {...props} initUserDefaultFilters={false} />
)

const SearchResultsLibraryRoute: React.FC = () => (
  <DNALibrary initUserDefaultFilters={false} />
)

const Routes = () => {
  const featureFlags = useFeatureFlags(
    'enableHubs',
    'enableSharedFolders',
    'enablePublisherReports',
    'enableMSLReports',
    'disablePublisherFolder',
    'BEAC_6094_hubs_templates',
    'enableOfflineMode',
    'BEAC_8524_beacon_bulletin',
  );

  return (
    <Switch>
      <Route exact path={ROUTES.SSO_OIDC_START.PATH} component={ExternalOidcVaultCrmSsoStart} />
      <Route exact path={ROUTES.SSO_OIDC_CALLBACK.PATH} component={ExternalOidcVaultCrmSsoCallback} />
      {/* Currently Home only pertains to the MSL, but could be different in the future */}
      <Route exact path={ROUTES.HOME.PATH} component={Home} />
      <ProtectedRoute
        path={ROUTES.PUBLISHER_DASHBOARD.PATH}
        component={Documents}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.PUBLISHER_DASHBOARD.ALLOWED_ROLES}
      />
      <ProtectedRoute
        path={ROUTES.TENANTS.PATH}
        component={Tenants}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.TENANTS.ALLOWED_ROLES}
      />
      <ProtectedRoute
        path={ROUTES.INTEGRATIONS.PATH}
        component={Tenants}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.INTEGRATIONS.ALLOWED_ROLES}
      />
      <ProtectedRoute
        path={ROUTES.EMAIL_TEMPLATES.PATH}
        component={EmailTemplates}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.EMAIL_TEMPLATES.ALLOWED_ROLES}
      />
      <ProtectedRoute
        path={ROUTES.HUB_TEMPLATES.PATH}
        component={featureFlags.BEAC_6094_hubs_templates ? HubsTemplateList : NotFound}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.HUB_TEMPLATES.ALLOWED_ROLES}
        exact
      />
      <ProtectedRoute
        path={ROUTES.EDIT_HUB_TEMPLATES.PATH}
        component={featureFlags.BEAC_6094_hubs_templates ? HubsTemplateList : NotFound}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.EDIT_HUB_TEMPLATES.ALLOWED_ROLES}
        exact
      />
      <ProtectedRoute
        path={ROUTES.REPORTS.PATH}
        component={featureFlags.enablePublisherReports ? PublisherReports : NotFound}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.REPORTS.ALLOWED_ROLES}
      />
      <ProtectedRoute
        path={ROUTES.SEARCH_RESULTS_PUBLISHER.PATH}
        component={PublisherSearchResultsPage}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.SEARCH_RESULTS_PUBLISHER.ALLOWED_ROLES}
      />
      <ProtectedRoute
        path={ROUTES.UI2_PUBLISHER_ADMIN.PATH}
        component={AdminSettings}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.UI2_PUBLISHER_ADMIN.ALLOWED_ROLES}
      />
      <ProtectedRoute
        path={ROUTES.UI2_PUBLISHER_ADMIN_USER.PATH}
        component={AdminSettings}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.UI2_PUBLISHER_ADMIN_USER.ALLOWED_ROLES}
      />
      <ProtectedRoute
        path={ROUTES.UI2_PUBLISHER_ADMIN_INTEGRATION.PATH}
        component={AdminSettings}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.UI2_PUBLISHER_ADMIN_INTEGRATION.ALLOWED_ROLES}
      />
      <ProtectedRoute
        path={ROUTES.UI2_PUBLISHER_AI_BEACON.PATH}
        component={PublisherCompliance}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.UI2_PUBLISHER_AI_BEACON.ALLOWED_ROLES}
      />
      <Route path={ROUTES.GENERAL_DEBUG.PATH} component={Debug} />
      <Route path={ROUTES.DEVICE_DEBUG.PATH} component={DeviceDebug} />
      <Route path={ROUTES.PLAYGROUND.PATH} component={Playground} />
      <Route path={ROUTES.LIBRARY.PATH} component={DNALibrary} exact />
      <Route path={ROUTES.VIEWER_REPORTS.PATH} component={featureFlags.enableMSLReports ? ViewerReports : NotFound} />
      <Route path={ROUTES.UI2_FAVORITE_FILES.PATH} component={DNABookmarks} exact />
      <Route path={ROUTES.UI2_FAVORITE_FOLDERS.PATH} component={DNAFavoriteFolders} exact />
      <ProtectedRoute
        path={ROUTES.MEETING_HISTORY.PATH}
        component={DNAMeetingsHistory}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.MEETING_HISTORY.ALLOWED_ROLES}
      />
      <Route path={ROUTES.HUBS.PATH} component={featureFlags.enableHubs ? DNAHubList : NotFound} exact />
      <Route path={ROUTES.EDIT_HUBS.PATH} component={featureFlags.enableHubs ? DNAHubList : NotFound} exact />
      <ProtectedRoute
        path={ROUTES.FOLDERS.PATH}
        component={DNAMyFolders}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.FOLDERS.ALLOWED_ROLES}
      />
      <ProtectedRoute
        path={ROUTES.SHARED_FOLDERS.PATH}
        component={featureFlags.enableSharedFolders ? DNAMyFolders : NotFound}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.SHARED_FOLDERS.ALLOWED_ROLES}
      />
      <ProtectedRoute
        path={ROUTES.ARCHIVED_FOLDERS.PATH}
        component={DNAMyFolders}
        onInvalidRedirectTo={'/'}
        allowedRoles={ROUTES.ARCHIVED_FOLDERS.ALLOWED_ROLES}
      />
      <Route
        path={ROUTES.PUBLISHER_FOLDERS.PATH}
        component={!featureFlags.disablePublisherFolder ? DNAMyFolders : NotFound}
      />
      <Route
        path={ROUTES.PUBLISHER_SHARED_FOLDERS.PATH}
        component={featureFlags.enableSharedFolders ? DNAMyFolders : NotFound}
      />
      <Route
        path={ROUTES.PUBLISHER_ARCHIVED_FOLDERS.PATH}
        component={!featureFlags.disablePublisherFolder ? DNAMyFolders : NotFound}
      />
      {/* We're using DNALibrary to handle both search and non-search views. We will probably refactor this later in the future. */}
      <Route
        path={ROUTES.SEARCH_RESULTS.PATH}
        component={SearchResultsLibraryRoute}
      />
      <Route path={ROUTES.MEETING_PRESENTATION.PATH} component={Meetings} />
      <Route
        path={ROUTES.MEETING_CONTENT.PATH}
        component={PopoutContent}
      />
      <Route path={ROUTES.FAQ.PATH} component={FAQ} />
      <ProtectedRoute exact path={ROUTES.BULLETIN.PATH}
        component={featureFlags.BEAC_8524_beacon_bulletin ? BulletinHistory : NotFound}
        allowedRoles={ROUTES.BULLETIN.ALLOWED_ROLES} onInvalidRedirectTo="/" />
      <Route path={ROUTES.NOTIFICATIONS.PATH} component={DNANotifications} />
      <Route path={ROUTES.PROFILE.PATH} component={Profile} />
      <Route
        path={ROUTES.SYNC_STATUS_DEBUG.PATH}
        component={featureFlags.enableOfflineMode ? SyncStatusDebug : NotFound}
      />
      <Route path="/." component={PerformanceWorkaroundRoute} />
      {/* Beacon Copy Link Routes */}
      <Route path={ROUTES.BEACON_COPY_LINK.PATH} component={DNALibrary} />
      <Route path={ROUTES.BEACON_COPY_LINK_BOOKMARKS.PATH} component={DNABookmarks} />
      <Route path={ROUTES.BEACON_COPY_LINK_HOME.PATH} component={Home} exact />
      {/* 404 Route */}
      <Route path={ROUTES.NOT_FOUND.PATH} component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
};

export const RoutesListener = () => {
  const history = useHistory();
  const currentUser = useCurrentUser();

  if (history.location.pathname === '/' && currentUser?.userProfile?.role === UserRole.ALUCIO_ADMIN) {
    history.push('/tenants');
  }

  analytics.page(history.location.pathname);

  return null;
};

export default Routes;
